import type { PortalApi, StudioApi } from './client';
import type { AuditFilter, ClientRole, Message, MessagePage, Person, StudioRole, Thread, WebhookDeliveryFilter } from './types';
import type { ProfileApi } from './profiles';
import { decryptSecret, encryptSecret } from '@/lib/crypto';

// Live implementation over portal-bff. Money comes from Invoice Ninja, tasks
// from Linear — the browser only ever talks to the BFF.

const USER_KEY = 'portal.user';

export function currentUser(): string | null {
  try {
    return localStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
}

export function signInAs(id: string | null) {
  try {
    if (id) localStorage.setItem(USER_KEY, id);
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* private mode: stays signed in for this tab only */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(base: string, path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const user = currentUser();
  if (user) headers.set('X-Portal-User', user);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  const r = await fetch(`${base}${path}`, { ...init, headers, body });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const j = await r.json();
      detail = typeof j.detail === 'string' ? j.detail : detail;
    } catch {
      /* not JSON */
    }
    if (r.status === 401) {
      signInAs(null);
      // Session over or never started: back to the sign-in page, then here again.
      if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/auth/')) {
        location.assign(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
      }
    }
    throw new ApiError(r.status, detail);
  }
  return (r.status === 204 ? undefined : r.json()) as T;
}

export type Me = { person: Person; side: 'client' | 'studio'; email: string; clientRole?: ClientRole; studioRole?: StudioRole };

/** Sign-in by e-mail link; the session itself is an HttpOnly cookie the page never sees. */
export const authApi = {
  me: () => call<Me>('/api/auth', '/me'),
  login: (email: string) => call<{ detail: string; devLink?: string }>('/api/auth', '/login', { method: 'POST', json: { email } }),
  callback: (token: string) => call<{ side: 'client' | 'studio' }>('/api/auth', '/callback', { method: 'POST', json: { token } }),
  logout: async () => {
    signInAs(null);
    await call('/api/auth', '/logout', { method: 'POST', json: {} });
  },
};

const qs = (o: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams();
  Object.entries(o).forEach(([k, v]) => v !== undefined && v !== '' && p.set(k, String(v)));
  const s = p.toString();
  return s ? `?${s}` : '';
};

const c = <T,>(path: string, init?: RequestInit & { json?: unknown }) => call<T>('/api/client', path, init);
const s = <T,>(path: string, init?: RequestInit & { json?: unknown }) => call<T>('/api/studio', path, init);
/** A file the BFF streams; the client fetches it with the session header and hands over a Blob. */
async function blobOf(path: string, failed: string, base = '/api/client'): Promise<Blob> {
  const headers = new Headers();
  const user = currentUser();
  if (user) headers.set('X-Portal-User', user);
  const r = await fetch(`${base}${path}`, { headers });
  if (!r.ok) throw new ApiError(r.status, failed);
  return r.blob();
}
const auditQs = (o: (AuditFilter & { limit?: number; before?: number; format?: string }) | undefined) =>
  qs({ limit: o?.limit, before: o?.before, actor: o?.actor, client_id: o?.clientId, action: o?.action, since: o?.since, until: o?.until, format: o?.format });
const post = (json?: unknown) => ({ method: 'POST', json: json ?? {} });
const whQs = (f?: WebhookDeliveryFilter) => qs({ endpointId: f?.endpointId, status: f?.status, event: f?.event, before: f?.before, limit: f?.limit });

/** What POST /threads answers: the new thread with its first message. */
type CreatedThread = { id: string; projectId: string; taskId: string | null; title: string; message: Message };
const asThread = (r: CreatedThread, body: string): Thread => ({
  id: r.id,
  projectId: r.projectId,
  taskId: r.taskId ?? undefined,
  title: r.title,
  lastMessageAt: r.message.at,
  lastMessage: body,
  lastAuthor: r.message.author,
  unread: 0,
  total: 1,
  awaitingClient: false,
});

const p = <T,>(path: string, init?: RequestInit & { json?: unknown }) => call<T>('/api', path, init);

export const httpProfileApi: ProfileApi = {
  profile: () => p('/me/profile'),
  saveProfile: (_side, patch) => p('/me/profile', { method: 'PATCH', json: patch }),
  sessions: () => p('/me/sessions'),
  revokeOthers: () => p('/me/sessions/revoke-others', post()),
  prefs: () => p('/me/prefs'),
  savePrefs: (prefs) => p('/me/prefs', { method: 'PUT', json: prefs }),
  calendar: () => p('/me/calendar'),
  resetCalendar: () => p('/me/calendar/reset', post()),
  person: (id) => p(`/people/${encodeURIComponent(id)}`),
};

export const httpApi: PortalApi = {
  workspace: () => c('/workspace'),
  actions: () => c('/actions'),
  search: (q) => c(`/search${qs({ q })}`),

  projects: () => c('/projects'),
  project: (id) => c(`/projects/${id}`),
  activity: (id) => c(`/projects/${id}/activity`),
  tasks: (projectId) => c(`/tasks${qs({ projectId })}`),
  task: (id) => c(`/tasks/${id}`),
  approveTask: (id) => c(`/tasks/${id}/approve`, post()),

  threads: (projectId) => c(`/threads${qs({ projectId })}`),
  messages: (threadId, opts) => c<MessagePage>(`/threads/${threadId}/messages${qs({ before: opts?.before, limit: opts?.limit })}`),
  sendMessage: (threadId, body, documentIds) => c<Message>(`/threads/${threadId}/messages`, post(documentIds?.length ? { body, documentIds } : { body })),
  markRead: (threadId) => c(`/threads/${threadId}/read`, post()),
  createThread: async (input) => asThread(await c<CreatedThread>('/threads', post(input)), input.body),

  invoices: () => c('/invoices'),
  invoice: (id) => c(`/invoices/${id}`),
  startCheckout: (invoiceId, method) => c(`/invoices/${invoiceId}/checkout`, post({ method })),
  checkoutStatus: (id) => c(`/checkouts/${id}`),
  invoicePdf: (id) => blobOf(`/invoices/${id}/pdf`, 'Счёт в PDF сейчас недоступен'),
  downloadDocument: (id) => blobOf(`/documents/${id}/file`, 'Файл сейчас недоступен'),
  downloadReportPdf: (id) => blobOf(`/reports/${id}/pdf`, 'PDF отчёта сейчас недоступен'),
  actFile: (id) => blobOf(`/acts/${id}/file`, 'PDF акта сейчас недоступен'),
  reportTransfer: (id) => c(`/invoices/${id}/transfer`, post()),
  acts: () => c('/acts'),
  signAct: (id, input) => c(`/acts/${id}/sign`, post(input)),
  uploadActScan: (id, file) => {
    const form = new FormData();
    form.set('file', file, file.name);
    return c(`/acts/${id}/scan`, { method: 'POST', body: form });
  },
  subscriptions: () => c('/subscriptions'),

  documents: (projectId) => c(`/documents${qs({ projectId })}`),
  uploadDocument: async ({ projectId, taskId, name, file }) => {
    const form = new FormData();
    form.set('file', file ?? new Blob([]), name);
    if (projectId) form.set('projectId', projectId);
    if (taskId) form.set('taskId', taskId);
    return c('/documents', { method: 'POST', body: form });
  },
  statementOfAccount: () => c('/statement-of-account'),

  secrets: () => c('/secrets'),
  sendSecret: async ({ title, toId, projectId, onboardingId, value, ttlHours }) => c('/secrets', post({ title, toId, projectId, onboardingId, ttlHours, sealed: await encryptSecret(value) })),
  openSecret: async (id) => decryptSecret((await c<{ sealed: Parameters<typeof decryptSecret>[0] }>(`/secrets/${id}/open`, post())).sealed),
  revokeSecret: (id) => c(`/secrets/${id}/revoke`, post()),
  rerequestSecret: async (id) => {
    await c(`/secrets/${id}/rerequest`, post());
  },

  proposals: () => c('/proposals'),
  proposal: (id) => c(`/proposals/${id}`),
  acceptProposal: (id) => c(`/proposals/${id}/accept`, post()),
  declineProposal: (id, reason) => c(`/proposals/${id}/decline`, post({ reason })),
  signRequests: () => c('/sign-requests'),
  sign: (id, name) => c(`/sign-requests/${id}/sign`, post({ name })),
  onboarding: (projectId) => c(`/onboarding${qs({ projectId })}`),
  completeOnboarding: (id) => c(`/onboarding/${id}/complete`, post()),
  changes: (projectId) => c(`/changes${qs({ projectId })}`),
  requestChange: (projectId, title, description, extra) => c('/changes', post({ projectId, title, description, ...extra })),
  answerChange: (id, text) => c(`/changes/${id}/answer`, post({ text })),
  decideChange: (id, accept) => c(`/changes/${id}/decide`, post({ accept })),
  change: (id) => c(`/changes/${id}`),
  reports: (projectId) => c(`/reports${qs({ projectId })}`),
  meetings: (projectId) => c(`/meetings${qs({ projectId })}`),
  meetingIcs: (id, series) => blobOf(`/meetings/${id}/ics${qs({ series: series ? '1' : undefined })}`, 'Файл календаря сейчас недоступен'),

  notifications: () => c('/notifications'),
  readNotifications: () => c('/notifications/read', post()),
  prefs: () => c('/prefs'),
  savePrefs: (p) => c('/prefs', { method: 'PUT', json: p }),
  payer: () => c('/payer'),
  savePayer: (p) => c('/payer', { method: 'PUT', json: p }),
  payers: () => c('/payers'),
  payerDetails: (id) => c(`/payers/${id}`),
  addPayer: (p) => c('/payers', post(p)),
  updatePayer: (id, p) => c(`/payers/${id}`, { method: 'PUT', json: p }),
  setDefaultPayer: (id) => c(`/payers/${id}/default`, post()),
  colleagues: () => c('/colleagues'),
  inviteColleague: (i) => c('/colleagues', post(i)),
  updateColleague: (id, patch) => c(`/colleagues/${id}`, { method: 'PATCH', json: patch }),
  resendInvitation: (id) => c(`/colleagues/${id}/resend`, post()),
  sendFeedback: (projectId, score, comment) => c('/feedback', post({ projectId, score, comment })),
  renew: (projectId) => c(`/projects/${projectId}/renew`, post()),
  apiKeys: () => c('/api-keys'),
  createApiKey: (input) => c('/api-keys', post(input)),
  rotateApiKey: (id) => c(`/api-keys/${id}/rotate`, post()),
  revokeApiKey: (id) => c(`/api-keys/${id}/revoke`, post()),
  apiDocs: () => c('/api-docs/openapi.json'),
  webhooks: () => c('/webhooks'),
  createWebhook: (input) => c('/webhooks', post(input)),
  updateWebhook: (id, patch) => c(`/webhooks/${id}`, { method: 'PATCH', json: patch }),
  deleteWebhook: (id) => c(`/webhooks/${id}`, { method: 'DELETE' }),
  rotateWebhookSecret: (id) => c(`/webhooks/${id}/rotate-secret`, post()),
  pingWebhook: (id) => c(`/webhooks/${id}/ping`, post()),
  webhookDeliveries: (f) => c(`/webhooks/deliveries${whQs(f)}`),
  replayWebhookDelivery: (id) => c(`/webhooks/deliveries/${id}/replay`, post()),
  replayFailedWebhooks: (id, since, until) => c(`/webhooks/${id}/replay-failed`, post({ since, until })),
};

export const httpStudioApi: StudioApi = {
  overview: () => s('/overview'),
  integrationsHealth: () => s('/health'),
  team: () => s('/team'),
  inviteTeammate: (input) => s('/team', post(input)),
  updatePerson: (id, patch) => s(`/people/${id}`, { method: 'PATCH', json: patch }),
  revokeSessions: (id) => s(`/people/${id}/revoke-sessions`, post()),
  anonymisePerson: (id) => s(`/people/${id}/anonymise`, post({ confirm: 'anonymise' })),
  personSessions: (id) => s(`/people/${id}/sessions`),
  people: () => s('/people'),
  audit: (opts) => s(`/audit${auditQs(opts)}`),
  auditCsv: (filter) => blobOf(`/audit${auditQs({ ...filter, format: 'csv' })}`, 'Не удалось выгрузить журнал', '/api/studio'),
  clients: () => s('/clients'),
  client: (id) => s(`/clients/${id}`),
  integrations: (clientId, offset = 0) => s(`/clients/${clientId}/integrations${qs({ offset: offset ? String(offset) : undefined })}`),
  setClientApi: (clientId, on) => s(`/clients/${clientId}/integrations`, { method: 'PUT', json: { apiEnabled: on } }),
  createClientApiKey: (clientId, input) => s(`/clients/${clientId}/integrations/keys`, post(input)),
  rotateClientApiKey: (clientId, keyId) => s(`/clients/${clientId}/integrations/keys/${keyId}/rotate`, post()),
  revokeClientApiKey: (clientId, keyId) => s(`/clients/${clientId}/integrations/keys/${keyId}/revoke`, post()),
  clientWebhooks: (clientId) => s(`/clients/${clientId}/webhooks`),
  clientWebhookDeliveries: (clientId, f) => s(`/clients/${clientId}/webhooks/deliveries${whQs(f)}`),
  disableClientWebhook: (clientId, endpointId) => s(`/clients/${clientId}/webhooks/${endpointId}/disable`, post()),
  createClient: (input) => s('/clients', post(input)),
  updateClient: (id, patch) => s(`/clients/${id}`, { method: 'PATCH', json: patch }),
  contour: () => s('/contour'),
  addContact: (clientId, input) => s(`/clients/${clientId}/contacts`, post(input)),
  addClientPayer: (clientId, input) => s(`/clients/${clientId}/payers`, post(input)),
  updateClientPayer: (clientId, payerId, input) => s(`/clients/${clientId}/payers/${payerId}`, { method: 'PATCH', json: input }),
  setClientDefaultPayer: (clientId, payerId) => s(`/clients/${clientId}/payers/${payerId}/default`, post()),
  inviteContact: (clientId, email) => s(`/clients/${clientId}/invite`, post({ email })),

  projects: (clientId) => s(`/projects${qs({ clientId })}`),
  project: (id) => s(`/projects/${id}`),
  createProject: (input) => s('/projects', post(input)),
  updateNow: (projectId, now, health) => s(`/projects/${projectId}/now`, { method: 'PUT', json: { now, health } }),
  updateProject: (id, patch) => s(`/projects/${id}`, { method: 'PATCH', json: patch }),
  deleteProject: (id) => s(`/projects/${id}`, { method: 'DELETE' }),
  linearProjects: () => s('/linear/projects'),
  linkLinear: (projectId, name) => s(`/projects/${projectId}/linear`, post({ name })),
  unlinkLinear: (projectId) => s(`/projects/${projectId}/linear`, { method: 'DELETE' }),
  rhythmDone: (projectId, rhythmId) => s(`/projects/${projectId}/rhythm/${rhythmId}/done`, post({})),
  setTasksVisible: (projectId, ids, visible) => s(`/projects/${projectId}/tasks/visibility`, post({ ids, visible })),
  documents: (opts) => s(`/documents${qs({ clientId: opts?.clientId, projectId: opts?.projectId })}`),
  uploadDocument: ({ clientId, projectId, file }) => {
    const form = new FormData();
    form.set('clientId', clientId);
    form.set('file', file, file.name);
    if (projectId) form.set('projectId', projectId);
    return s('/documents', { method: 'POST', body: form });
  },
  downloadDocument: (id) => blobOf(`/documents/${id}/file`, 'Файл сейчас недоступен', '/api/studio'),
  downloadReportPdf: (id) => blobOf(`/reports/${id}/pdf`, 'PDF отчёта сейчас недоступен', '/api/studio'),

  tasks: (projectId, opts) => s(`/projects/${projectId}/tasks${qs({ visible: opts?.visible ? '1' : undefined })}`),
  createTask: (input) => s('/tasks', post(input)),
  updateTask: (id, patch) =>
    s(`/tasks/${id}`, {
      method: 'PATCH',
      json: { clientVisible: patch.clientVisible, needsClient: patch.needsClient ?? undefined, clearNeeds: patch.needsClient === null },
    }),

  threads: (f) => s(`/threads${qs({ q: f?.q?.trim(), clientId: f?.clientId, projectId: f?.projectId, waiting: f?.waiting ? 'true' : undefined })}`),
  closeThread: (id) => s(`/threads/${id}/close`, post()),
  reply: (threadId, body, authorId) => s(`/threads/${threadId}/messages`, post({ body, authorId })),
  createThread: async (input) => asThread(await s<CreatedThread>('/threads', post(input)), input.body),

  catalog: () => s('/catalog'),
  invoices: (clientId) => s(`/invoices${qs({ clientId })}`),
  issueInvoice: (input) => s('/invoices', post(input)),
  updateDraftInvoice: (id, input) => s(`/invoices/${id}`, { method: 'PATCH', json: input }),
  deleteDraftInvoice: async (id) => {
    await s(`/invoices/${id}`, { method: 'DELETE' });
  },
  sendInvoice: (id) => s(`/invoices/${id}/send`, post()),

  proposals: (clientId) => s(`/proposals${qs({ clientId })}`),
  createProposal: (input) => s('/proposals', post(input)),
  sendForSignature: ({ clientId, kind, title, file, projectId }) => {
    const form = new FormData();
    form.set('clientId', clientId);
    form.set('kind', kind);
    form.set('title', title);
    form.set('file', file, file.name);
    if (projectId) form.set('projectId', projectId);
    return s('/sign-requests', { method: 'POST', body: form });
  },
  signRequests: () => s('/sign-requests'),

  onboarding: (projectId) => s(`/projects/${projectId}/onboarding`),
  addOnboarding: (projectId, item) => s(`/projects/${projectId}/onboarding`, post(item)),
  projectAccess: (projectId) => s(`/projects/${projectId}/access`),
  toggleOnboarding: (id) => s(`/onboarding/${id}/toggle`, post()),

  changes: (projectId) => s(`/changes${qs({ projectId })}`),
  declineChange: (id, reason) => s(`/changes/${id}/decline`, post({ reason })),
  change: (id) => s(`/changes/${id}`),
  reinvoiceChange: (id) => s(`/changes/${id}/reinvoice`, post()),
  retryChangeIssue: (id) => s(`/changes/${id}/linear`, post()),
  estimateChange: (id, estimate) => s(`/changes/${id}/estimate`, post(estimate === 'in_scope' ? { inScope: true } : estimate)),
  askChange: (id, text) => s(`/changes/${id}/ask`, post({ text })),
  parkChange: (id, note) => s(`/changes/${id}/park`, post({ text: note })),

  publishReport: (input) => s('/reports', post(input)),
  reports: (projectId) => s(`/projects/${projectId}/reports`),
  meetings: (projectId) => s(`/meetings${qs({ projectId })}`),
  planMeeting: (input) => s('/meetings', post(input)),
  editMeeting: (id, patch, scope = 'one') => s(`/meetings/${id}${qs({ scope })}`, { method: 'PATCH', json: patch }),
  cancelMeeting: (id, scope = 'one') => s(`/meetings/${id}/cancel${qs({ scope })}`, post()),
  recordOutcome: (id, outcome) => s(`/meetings/${id}/outcome`, post(outcome)),
  firefliesDraft: (id) => s(`/meetings/${id}/fireflies`, post()),
  meetingIcs: (id, series) => blobOf(`/meetings/${id}/ics${qs({ series: series ? '1' : undefined })}`, 'Файл календаря сейчас недоступен', '/api/studio'),

  checkoutProblems: () => s('/checkouts?problems=1'),
  resolveCheckout: (id, note) => s(`/checkouts/${id}/resolve`, post({ note })),
  undelivered: () => s('/notifications/undelivered'),
  retryNotification: (id) => s(`/notifications/${id}/retry`, post()),
  statement: () => s('/statement'),
  addStatementLine: (line) => s('/statement', post(line)),
  importStatement: (file) => {
    const form = new FormData();
    form.set('file', file, file.name);
    return s('/statement/import', { method: 'POST', body: form });
  },
  matchPayment: (lineId, picks) => s(`/statement/${lineId}/match`, post({ allocations: picks })),
  unmatchLine: (lineId) => s(`/statement/${lineId}/unmatch`, post()),
  ignoreLine: (lineId) => s(`/statement/${lineId}/ignore`, post()),
  remind: (invoiceId) => s(`/invoices/${invoiceId}/remind`, post()),
  markPaid: (invoiceId, input) => s(`/invoices/${invoiceId}/mark-paid`, post(input)),
  cancelInvoice: (invoiceId) => s(`/invoices/${invoiceId}/cancel`, post()),
  invoicePdf: (invoiceId) => blobOf(`/invoices/${invoiceId}/pdf`, 'Счёт в PDF сейчас недоступен', '/api/studio'),
  issueAct: (invoiceId) => s(`/invoices/${invoiceId}/act`, post()),

  studioActs: (f) => s(`/acts${qs({ clientId: f?.clientId, projectId: f?.projectId })}`),
  actSources: (q) => s(`/acts/sources${qs(q)}`),
  createAct: (clientId, input) => s('/acts', post({ clientId, ...input })),
  updateAct: (id, patch) => s(`/acts/${id}`, { method: 'PATCH', json: patch }),
  deleteAct: async (id) => {
    await s(`/acts/${id}`, { method: 'DELETE' });
  },
  sendAct: (id) => s(`/acts/${id}/send`, post()),
  voidAct: (id) => s(`/acts/${id}/void`, post()),
  markActSigned: (id, { via, signedOn, file }) => {
    const form = new FormData();
    form.set('via', via);
    if (signedOn) form.set('signedOn', signedOn);
    if (file) form.set('file', file, file.name);
    return s(`/acts/${id}/mark-signed`, { method: 'POST', body: form });
  },
  studioActPdf: (id) => blobOf(`/acts/${id}/pdf`, 'PDF акта сейчас недоступен', '/api/studio'),

  feedback: () => s('/feedback'),
  accessRegistry: (clientId) => s(`/clients/${clientId}/access`),
  revokeAccess: (id) => s(`/secrets/${id}/revoke`, post()),
  incomingSecrets: () => s('/secrets'),
  openSecret: async (id) => decryptSecret((await s<{ sealed: Parameters<typeof decryptSecret>[0] }>(`/secrets/${id}/open`, post())).sealed),
  offboard: (clientId) => s(`/clients/${clientId}/offboard`, post()),
};

/** Studio view of a thread's messages (the client endpoint is scoped to one client). */
export const studioMessages = (threadId: string, before?: string) => s<MessagePage>(`/threads/${threadId}/messages${qs({ before })}`);
